/**
 * CookieYes + Google Consent Mode v2 + GTM.
 *
 * Flow:
 *   1. CookieYes script loads first (gatsby-ssr.js).
 *   2. We set Consent Mode defaults = denied.
 *   3. User clicks Accept (or already accepted) → we update Consent Mode
 *      and load GTM. GTM-side tags fire per their consent settings.
 *
 * All pixels (Intercom, Meta, Bing, LinkedIn, GA, Clarity) live in GTM.
 * Unify Intent loads from this app after the same consent grant.
 */

export const COOKIEYES_SCRIPT_ID = process.env.GATSBY_COOKIE_YES_SITE_ID;
export const COOKIEYES_SCRIPT_SRC = COOKIEYES_SCRIPT_ID
  ? `https://cdn-cookieyes.com/client_data/${COOKIEYES_SCRIPT_ID}/script.js`
  : "";

/** Must not be "cookieyes" — that id shadows window.cookieyes (the SDK). */
export const COOKIEYES_SCRIPT_ELEMENT_ID = "cookieyes-client-script";

export const GTM_ID = process.env.GATSBY_GTM_TAG;

const UNIFY_SCRIPT_SRC =
  "https://tag.unifyintent.com/v1/NP9MZddSBWJNUWLSK8WbQR/script.js";
const UNIFY_API_KEY = "wk_94cpMUvr_HpJsgSokxUyCxM4ihHDCs6vW7b5tkEee";
const UNIFY_SCRIPT_ID = "unifytag";
const UNIFY_QUEUE_METHODS = [
  "identify",
  "page",
  "startAutoPage",
  "stopAutoPage",
  "startAutoIdentify",
  "stopAutoIdentify",
];

const DEFAULT_CATEGORIES = Object.freeze({
  necessary: true,
  analytics: false,
  advertisement: false,
  functional: false,
});

/** Read consent from whichever CookieYes API is available. */
function readCookieYesConsent() {
  if (typeof window === "undefined") return null;
  if (typeof window.getCkyConsent === "function") {
    try {
      return window.getCkyConsent();
    } catch (_) {
      return null;
    }
  }
  if (typeof window.cookieyes?.getConsent === "function") {
    try {
      return window.cookieyes.getConsent();
    } catch (_) {
      return null;
    }
  }
  return null;
}

/** Normalize CookieYes payload (categories object OR accepted[] array). */
export function parseCookieYesCategories(payload) {
  if (!payload) return { ...DEFAULT_CATEGORIES };

  const accepted = Array.isArray(payload.accepted) ? payload.accepted : null;
  if (accepted && accepted.length > 0) {
    return {
      necessary: true,
      analytics:
        accepted.includes("analytics") || accepted.includes("performance"),
      advertisement:
        accepted.includes("advertisement") || accepted.includes("advertising"),
      functional:
        accepted.includes("functional") || accepted.includes("preferences"),
    };
  }

  const c = payload.categories;
  if (c && typeof c === "object") {
    return {
      necessary: c.necessary !== false,
      analytics: Boolean(c.analytics || c.performance),
      advertisement: Boolean(c.advertisement),
      functional: Boolean(c.functional),
    };
  }

  return { ...DEFAULT_CATEGORIES };
}

export function getConsentCategories() {
  return parseCookieYesCategories(readCookieYesConsent());
}

export const hasAnalyticsConsent = () => getConsentCategories().analytics;
export const hasMarketingConsent = () => getConsentCategories().advertisement;
export const hasFunctionalConsent = () => getConsentCategories().functional;
export const hasTrackingConsentForEvents = () => {
  const c = getConsentCategories();
  return c.analytics || c.advertisement || c.functional;
};

let consentDefaultsSet = false;
let gtmLoaded = false;
let unifyLoaded = false;

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

export function initGoogleConsentModeDefaults() {
  if (typeof window === "undefined" || consentDefaultsSet) return;
  ensureGtag();
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });
  consentDefaultsSet = true;
}

function consentFlags(c) {
  return {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.advertisement ? "granted" : "denied",
    ad_user_data: c.advertisement ? "granted" : "denied",
    ad_personalization: c.advertisement ? "granted" : "denied",
    functionality_storage: c.functional ? "granted" : "denied",
    personalization_storage: c.advertisement ? "granted" : "denied",
  };
}

export function updateGoogleConsentMode(categories) {
  if (typeof window === "undefined") return;
  initGoogleConsentModeDefaults();
  ensureGtag();
  window.gtag("consent", "update", consentFlags(categories));
  window.dataLayer.push({
    event: "consent_update",
    ...consentFlags(categories),
  });
}

export function loadGoogleTagManager() {
  if (typeof window === "undefined" || gtmLoaded) return;
  if (!GTM_ID) return;
  if (window.google_tag_manager?.[GTM_ID]) {
    gtmLoaded = true;
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);

  const ns = document.createElement("noscript");
  ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  if (document.body) document.body.appendChild(ns);

  gtmLoaded = true;
}

function createUnifyStub(existing) {
  const queue = Array.isArray(existing) ? existing : [];
  return Object.assign(
    queue,
    UNIFY_QUEUE_METHODS.reduce((acc, method) => {
      acc[method] = function unifyStub() {
        queue.push([method, [].slice.call(arguments)]);
        return queue;
      };
      return acc;
    }, {})
  );
}

/** Unify Intent website tag — same snippet Unify provides for GTM/HTML. */
export function loadUnifyIntent() {
  if (typeof window === "undefined" || unifyLoaded) return;
  if (document.getElementById(UNIFY_SCRIPT_ID)) {
    unifyLoaded = true;
    return;
  }

  window.unify = window.unify || createUnifyStub(window.unify);
  window.unifyBrowser =
    window.unifyBrowser || createUnifyStub(window.unifyBrowser);

  const script = document.createElement("script");
  script.async = true;
  script.id = UNIFY_SCRIPT_ID;
  script.setAttribute("src", UNIFY_SCRIPT_SRC);
  script.setAttribute("data-api-key", UNIFY_API_KEY);
  (document.body || document.head).appendChild(script);

  unifyLoaded = true;
}

function setConsentDomFlags(c) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.cookieyesAnalytics = c.analytics ? "true" : "false";
  root.dataset.cookieyesAdvertisement = c.advertisement ? "true" : "false";
  root.dataset.cookieyesFunctional = c.functional ? "true" : "false";
}

/**
 * Single entry point — call on banner load, on consent update, on mount.
 * If the user has accepted anything, push consent + load GTM and Unify.
 */
export function applyConsentToTracking(payloadFromEvent) {
  const fromApi = readCookieYesConsent();
  const categories = parseCookieYesCategories(fromApi || payloadFromEvent);

  setConsentDomFlags(categories);
  updateGoogleConsentMode(categories);

  if (
    categories.analytics ||
    categories.advertisement ||
    categories.functional
  ) {
    loadGoogleTagManager();
    loadUnifyIntent();
  }
}
